import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { getText, getStyle, getMedia, getCropStyle, mergeClients, mergeMedia } from '../config/overrides';
import { toMediaUrl, isVideoSrc, inferMediaKind } from '../utils/mediaUrl';

const useReducedMotion = () => {
  const [r, setR] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setR(mq.matches);
    const h = (e) => setR(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);
  return r;
};

/* ── Preview Modal (image or video) ──────── */
const PreviewModal = ({ item, onClose }) => {
  const [mediaError, setMediaError] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Reset states when item changes
  useEffect(() => { setMediaError(false); setMediaLoaded(false); }, [item]);

  // Determine actual media type: use inferMediaKind for robust detection
  const kind = inferMediaKind(item);
  const isVideo = kind === 'video';
  const resolvedSrc = toMediaUrl(item.src);
  const resolvedPoster = item.poster ? toMediaUrl(item.poster) : undefined;

  // If src resolved to empty, show error immediately (never blank)
  const srcMissing = !resolvedSrc;

  return (
    <motion.div
      className="video-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isVideo ? 'Lecteur vidéo' : 'Aperçu image'}
      data-testid="preview-modal"
    >
      <motion.div
        className="video-modal-content"
        initial={{ scale: 0.94, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="video-modal-close"
          onClick={onClose}
          aria-label="Fermer"
          data-testid="preview-modal-close"
        >
          <X size={20} strokeWidth={1.5} />
        </button>
        {/* Loading spinner — shown until media loads or errors */}
        {!mediaLoaded && !mediaError && !srcMissing && (
          <div className="media-loading-indicator" aria-label="Chargement…">
            <div className="media-loading-spinner" />
          </div>
        )}
        {(mediaError || srcMissing) ? (
          <div className="media-error-fallback">
            {isVideo
              ? 'Vidéo non disponible — essayez sur un autre appareil ou fournissez un MP4 H.264.'
              : 'Image non disponible.'}
          </div>
        ) : isVideo ? (
          <video
            src={resolvedSrc}
            poster={resolvedPoster}
            controls
            autoPlay
            playsInline
            preload="metadata"
            disablePictureInPicture
            className="video-modal-player"
            onLoadedData={() => setMediaLoaded(true)}
            onError={() => setMediaError(true)}
          />
        ) : (
          <img
            src={resolvedSrc}
            alt=""
            className="preview-modal-img"
            decoding="async"
            fetchpriority="high"
            onLoad={() => setMediaLoaded(true)}
            onError={(e) => {
              const el = e.currentTarget;
              const curSrc = el.getAttribute('src') || '';
              if (curSrc.includes('_r=1')) {
                setMediaError(true);
              } else {
                setTimeout(() => {
                  if (el.isConnected) {
                    el.src = resolvedSrc + (resolvedSrc.includes('?') ? '&' : '?') + '_r=1';
                  }
                }, 500);
              }
            }}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

/* ── Gallery Carousel ────────────────────── */
const SLOT_CONFIGS = [
  { xPct: -0.17, scale: 0.65, opacity: 1, z: 1 },
  { xPct: -0.12, scale: 0.8, opacity: 1, z: 3 },
  { xPct: 0, scale: 1, opacity: 1, z: 5 },
  { xPct: 0.12, scale: 0.8, opacity: 1, z: 3 },
  { xPct: 0.17, scale: 0.65, opacity: 1, z: 1 },
];

const GalleryCarousel = ({ items, reducedMotion, variant, clientId }) => {
  const [centerIdx, setCenterIdx] = useState(0);
  const [modalItem, setModalItem] = useState(null);
  const [brokenSrcs, setBrokenSrcs] = useState(new Set());
  const [videoFailed, setVideoFailed] = useState(new Set()); // loop video fallback
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(900);
  const touchStartX = useRef(0);
  const touchStartTime = useRef(0);
  const lastNavTime = useRef(0);
  const isAnimating = useRef(false);
  const wheelAccum = useRef(0);
  const wheelRaf = useRef(null);
  const count = items.length;

  // Clear broken/failed caches when client changes (key prop remounts, but defensive)
  useEffect(() => {
    setBrokenSrcs(new Set());
    setVideoFailed(new Set());
  }, [clientId]);

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) setTrackWidth(trackRef.current.offsetWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Apply media overrides to all items (items have _id from mergeMedia)
  const overriddenItems = useMemo(() => {
    if (!variant || !clientId) return items;
    return items.map((item) => {
      const editId = `state3.${variant}.client.${clientId}.media.${item._id}`;
      const merged = { ...getMedia(editId, item), _id: item._id };
      // Defensive normalization: ensure type is always set
      if (!merged.type && !merged.kind) {
        merged.type = (merged.src && isVideoSrc(merged.src)) ? 'video' : 'image';
      }
      return merged;
    });
  }, [items, variant, clientId]);

  const navigate = useCallback((d) => {
    const now = Date.now();
    if (now - lastNavTime.current < 280) return;
    lastNavTime.current = now;
    isAnimating.current = true;
    setCenterIdx((prev) => (prev + d + count) % count);
    setTimeout(() => { isAnimating.current = false; }, 400);
  }, [count]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    const elapsed = Date.now() - touchStartTime.current;
    // Velocity-aware: fast swipes need less distance
    const minDist = elapsed < 200 ? 20 : 40;
    if (Math.abs(diff) > minDist) {
      navigate(diff > 0 ? 1 : -1);
    }
  }, [navigate]);

  // Wheel / trackpad navigation — accumulator + rAF for smooth trackpad feel
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const handler = (e) => {
      e.preventDefault();
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      wheelAccum.current += delta;
      if (wheelRaf.current) return; // already scheduled
      wheelRaf.current = requestAnimationFrame(() => {
        wheelRaf.current = null;
        const accum = wheelAccum.current;
        wheelAccum.current = 0;
        if (Math.abs(accum) < 12) return; // noise gate
        navigate(accum > 0 ? 1 : -1);
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => {
      el.removeEventListener('wheel', handler);
      if (wheelRaf.current) cancelAnimationFrame(wheelRaf.current);
    };
  }, [navigate]);

  const focusCard = useCallback((itemIdx) => {
    if (isAnimating.current) return;
    lastNavTime.current = Date.now();
    isAnimating.current = true;
    setCenterIdx(itemIdx);
    setTimeout(() => { isAnimating.current = false; }, 400);
  }, []);

  const handleCardClick = useCallback((itemIdx, slotOffset) => {
    if (slotOffset === 0) {
      // Animation guard: don't open modal if carousel is still settling
      if (Date.now() - lastNavTime.current < 380) return;
      const item = overriddenItems[itemIdx];
      // Only open modal if the item has a source (not a placeholder)
      if (item && item.src) {
        setModalItem(item);
      }
    } else {
      focusCard(itemIdx);
    }
  }, [overriddenItems, focusCard]);

  const springConfig = reducedMotion
    ? { duration: 0.15 }
    : { type: 'spring', stiffness: 280, damping: 36, mass: 1, restDelta: 0.001, restSpeed: 0.01 };

  // Build visible cards: up to 5 slots, but NEVER more than the real item count.
  // This prevents duplicate slots (which caused all 5 to share the same item/data-edit-id
  // when a new client had only 1 card, making edits apply to all visible slots at once).
  const visibleCount = Math.min(count, 5);
  // Center slot index within the visible window (e.g. 2 for 5 slots, 0 for 1 slot)
  const centerSlotIdx = Math.floor(visibleCount / 2);
  const visibleCards = [];
  for (let slot = 0; slot < visibleCount; slot++) {
    const offset = slot - centerSlotIdx;
    const itemIdx = ((centerIdx + offset) % count + count) % count;
    // Pick the SLOT_CONFIGS entry that corresponds to this slot's position in a 5-slot layout,
    // anchored so the center slot always uses SLOT_CONFIGS[2] (the "hero" position).
    const cfgIdx = slot - centerSlotIdx + 2;
    const cfg = SLOT_CONFIGS[Math.max(0, Math.min(4, cfgIdx))];
    const xPx = cfg.xPct * trackWidth;

    visibleCards.push({
      slot,
      offset,
      itemIdx,
      item: overriddenItems[itemIdx],
      x: xPx,
      scale: cfg.scale,
      opacity: cfg.opacity,
      z: cfg.z,
    });
  }

  // Render in slot order (stable DOM); stacking handled by zIndex style
  const renderCards = visibleCards;

  /**
   * Get the thumbnail source for a media item (already overridden).
   * Never returns a video file URL — <img> cannot render video files.
   *
   * Rules:
   *  - video item → show poster. No poster → placeholder.
   *  - image item → show src. If src is actually a video file → use poster → placeholder.
   *  - No src and no poster → placeholder.
   */
  const getThumb = (item) => {
    if (!item) return null;
    if (!item.src && !item.poster) return null; // truly empty card

    const kind = inferMediaKind(item);

    if (kind === 'video') {
      // Prefer poster; never use the video src for <img>
      if (item.poster) return toMediaUrl(item.poster);
      // No poster — check if src is actually an image (rare but possible mistype)
      if (item.src && !isVideoSrc(item.src)) return toMediaUrl(item.src);
      // Video with no usable poster → null (show placeholder)
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[BY ZY] Video item has no poster — card will show placeholder`, item);
      }
      return null;
    }

    // Image item: verify src is not actually a video file (type mismatch)
    if (item.src && isVideoSrc(item.src)) {
      if (item.poster) return toMediaUrl(item.poster);
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[BY ZY] Image item has video src — card will show placeholder`, item);
      }
      return null;
    }

    return toMediaUrl(item.src);
  };

  return (
    <>
      <div
        className="gallery-track"
        ref={trackRef}
        data-testid="gallery-track"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {renderCards.map((card) => {
          const isCenter = card.offset === 0;
          const itemId = card.item._id != null ? card.item._id : card.itemIdx;
          const mediaEditId = variant && clientId
            ? `state3.${variant}.client.${clientId}.media.${itemId}`
            : undefined;
          const cropStyle = mediaEditId ? getCropStyle(mediaEditId) : undefined;
          const rawThumb = getThumb(card.item);
          // If this src previously failed to load, treat it as missing
          const thumb = rawThumb && !brokenSrcs.has(rawThumb) ? rawThumb : null;
          const isVideo = inferMediaKind(card.item) === 'video';

          // Loop video preview mode: use preview.videoSrc or the main src (for videos)
          const previewMode = card.item.preview?.mode || 'poster';
          const previewFit = card.item.preview?.fit || 'cover';
          const previewPosition = card.item.preview?.position || '50% 50%';
          const itemKey = `${itemId}`;
          const useLoopVideo = previewMode === 'loopVideo'
            && !reducedMotion
            && !videoFailed.has(itemKey);
          // For loop video: prefer preview.videoSrc, then src (if video), then nothing
          const loopVideoSrc = useLoopVideo
            ? toMediaUrl(card.item.preview?.videoSrc || (isVideo ? card.item.src : null))
            : null;

          const cardStyle = {
            ...(cropStyle || {}),
            objectFit: previewFit,
            objectPosition: previewPosition,
          };

          return (
            <motion.div
              key={`item-${itemId}`}
              className={`gallery-card ${isCenter ? 'gallery-card--center' : ''}`}
              animate={{
                x: card.x,
                scale: card.scale,
                opacity: card.opacity,
              }}
              transition={springConfig}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                zIndex: card.z,
                cursor: 'pointer',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
              }}
              onClick={() => handleCardClick(card.itemIdx, card.offset)}
              data-testid={isCenter ? 'gallery-center-card' : `gallery-card-${card.slot}`}
              data-edit-id={mediaEditId}
            >
              {loopVideoSrc ? (
                /* Loop video preview mode */
                <video
                  src={loopVideoSrc}
                  poster={thumb || undefined}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  disablePictureInPicture
                  className="gallery-card-img"
                  draggable={false}
                  style={cardStyle}
                  onError={() => {
                    setVideoFailed((prev) => new Set(prev).add(itemKey));
                  }}
                  onStalled={() => {
                    setVideoFailed((prev) => new Set(prev).add(itemKey));
                  }}
                />
              ) : (isVideo && !thumb && card.item.src) ? (
                /* Video without poster: show first frame via non-autoplay video */
                <video
                  src={toMediaUrl(card.item.src)}
                  muted
                  playsInline
                  preload="metadata"
                  disablePictureInPicture
                  className="gallery-card-img"
                  draggable={false}
                  style={cardStyle}
                />
              ) : thumb ? (
                <img
                  src={thumb}
                  alt=""
                  className="gallery-card-img"
                  draggable={false}
                  decoding="async"
                  loading={Math.abs(card.offset) <= 1 ? 'eager' : 'lazy'}
                  fetchpriority={isCenter ? 'high' : undefined}
                  style={cardStyle}
                  onError={(e) => {
                    const el = e.currentTarget;
                    const curSrc = el.getAttribute('src') || '';
                    if (curSrc.includes('_r=1')) {
                      // Already retried once — give up, show placeholder
                      setBrokenSrcs((prev) => new Set(prev).add(rawThumb));
                    } else {
                      // Single retry with cache-bust after short delay
                      setTimeout(() => {
                        if (el.isConnected) {
                          el.src = rawThumb + (rawThumb.includes('?') ? '&' : '?') + '_r=1';
                        }
                      }, 500);
                    }
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.01))',
                    borderRadius: 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    color: 'rgba(255,255,255,0.2)',
                    border: '1px dashed rgba(255,255,255,0.1)',
                  }}
                >
                  {isVideo ? <Play size={20} style={{ opacity: 0.35 }} /> : 'Empty'}
                </div>
              )}
              {isVideo && (thumb || loopVideoSrc || card.item.src) && (
                <div className="gallery-video-badge" aria-label="Vidéo">
                  <Play size={16} fill="white" strokeWidth={0} />
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="gallery-nav" data-testid="gallery-nav">
        <motion.button
          className="gallery-nav-btn"
          onClick={() => navigate(-1)}
          whileTap={reducedMotion ? {} : { scale: 0.9 }}
          aria-label="Média précédent"
          data-testid="gallery-prev"
        >
          <ChevronLeft size={18} strokeWidth={1.5} />
        </motion.button>
        <motion.button
          className="gallery-nav-btn"
          onClick={() => navigate(1)}
          whileTap={reducedMotion ? {} : { scale: 0.9 }}
          aria-label="Média suivant"
          data-testid="gallery-next"
        >
          <ChevronRight size={18} strokeWidth={1.5} />
        </motion.button>
      </div>

      {createPortal(
        <AnimatePresence>
          {modalItem && <PreviewModal item={modalItem} onClose={() => setModalItem(null)} />}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

/* ── Detail State (State 3) ──────────────── */
export const DetailState = ({ variant, onBack, categoryData, adminMode, onReorderClients, onOpenQuote }) => {
  const [selectedClient, setSelectedClient] = useState(null);
  const reducedMotion = useReducedMotion();

  // categoryData comes from the content loader
  // In admin: already merged by EditorApp
  // In public: apply mergeClients here
  const category = categoryData || { title: '', subtitle: '', clients: [] };
  const clients = adminMode
    ? (category.clients || [])
    : mergeClients(category.clients || [], variant);

  const chipRefs = useRef({});

  // ---- Drag-and-drop state (admin mode only) ----
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const handleChipClick = useCallback((id) => {
    setSelectedClient((prev) => (prev === id ? prev : id));
    // Scroll selected chip into view smoothly (only when overflow exists)
    requestAnimationFrame(() => {
      const el = chipRefs.current[id];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    });
  }, []);

  // ---- Drag handlers (admin mode only) ----
  const handleDragStart = useCallback((e, clientId) => {
    setDragId(clientId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', clientId);
  }, []);

  const handleDragOver = useCallback((e, clientId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(clientId);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e, targetId) => {
    e.preventDefault();
    setDragOverId(null);
    const sourceId = dragId;
    if (!sourceId || sourceId === targetId) {
      setDragId(null);
      return;
    }

    // Compute new order
    const currentIds = clients.map(c => c.id);
    const sourceIdx = currentIds.indexOf(sourceId);
    const targetIdx = currentIds.indexOf(targetId);
    if (sourceIdx === -1 || targetIdx === -1) {
      setDragId(null);
      return;
    }

    const newOrder = [...currentIds];
    newOrder.splice(sourceIdx, 1);
    newOrder.splice(targetIdx, 0, sourceId);

    onReorderClients?.(variant, newOrder);
    setDragId(null);
  }, [dragId, clients, onReorderClients, variant]);

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setDragOverId(null);
  }, []);

  const activeClient = clients.find((c) => c.id === selectedClient);
  // Merge media items: base + added - deleted, with ordering and stable _id
  const activeMedia = useMemo(() => {
    if (!activeClient) return [];
    return mergeMedia(activeClient.media || [], variant, activeClient.id);
  }, [activeClient, variant]);
  const hasSelection = !!activeClient;

  const ease = [0.25, 0.1, 0.25, 1];

  return (
    <div className="detail-state" data-testid="detail-state">
      <motion.button
        className="detail-back"
        onClick={onBack}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        whileTap={reducedMotion ? {} : { scale: 0.96 }}
        transition={{ duration: 0.3, delay: 0.1, ease }}
        aria-label="Retour"
        data-testid="detail-back"
      >
        <ArrowLeft size={18} strokeWidth={1.5} />
        <span>Retour</span>
      </motion.button>

      <motion.div
        className="detail-header"
        animate={hasSelection ? { y: 0 } : { y: '12vh' }}
        transition={{ duration: reducedMotion ? 0.15 : 0.45, ease }}
        data-testid="detail-header"
      >
        <motion.span
          className="detail-label"
          data-edit-id={`state3.${variant}.label`}
          style={getStyle(`state3.${variant}.label`)}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.05, ease }}
        >
          {getText(`state3.${variant}.label`, 'GALLERY')}
        </motion.span>
        <motion.h1
          className="detail-title"
          data-edit-id={`state3.${variant}.title`}
          style={getStyle(`state3.${variant}.title`)}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease }}
        >
          {getText(`state3.${variant}.title`, category.title)}
        </motion.h1>
        <motion.p
          className="detail-subtitle"
          data-edit-id={`state3.${variant}.subtitle`}
          style={getStyle(`state3.${variant}.subtitle`)}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.18, ease }}
        >
          {getText(`state3.${variant}.subtitle`, category.subtitle)}
        </motion.p>
      </motion.div>

      <motion.div
        className="detail-chips-wrap"
        animate={hasSelection ? { y: 0 } : { y: '12vh' }}
        transition={{ duration: reducedMotion ? 0.15 : 0.45, ease }}
      >
        <div className="detail-chips-outer">
          <div className="detail-chips-scroll">
            <div className="detail-chips" data-testid="detail-chips">
              {clients.map((c, i) => (
                <motion.button
                  key={c.id}
                  ref={(el) => { chipRefs.current[c.id] = el; }}
                  className={`detail-chip ${selectedClient === c.id ? 'detail-chip--active' : ''} ${dragOverId === c.id ? 'detail-chip--drag-over' : ''} ${dragId === c.id ? 'detail-chip--dragging' : ''}`}
                  onClick={() => handleChipClick(c.id)}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  whileTap={reducedMotion ? {} : { scale: 0.95 }}
                  transition={{ duration: 0.35, delay: 0.2 + i * 0.04, ease }}
                  data-testid={`chip-${c.id}`}
                  data-edit-id={`state3.${variant}.client.${c.id}.name`}
                  style={getStyle(`state3.${variant}.client.${c.id}.name`)}
                  title={c.name}
                  draggable={adminMode ? true : undefined}
                  onDragStart={adminMode ? (e) => handleDragStart(e, c.id) : undefined}
                  onDragOver={adminMode ? (e) => handleDragOver(e, c.id) : undefined}
                  onDragLeave={adminMode ? handleDragLeave : undefined}
                  onDrop={adminMode ? (e) => handleDrop(e, c.id) : undefined}
                  onDragEnd={adminMode ? handleDragEnd : undefined}
                >
                  {getText(`state3.${variant}.client.${c.id}.name`, c.name)}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="detail-gallery-wrap"
        initial={false}
        animate={{
          opacity: hasSelection ? 1 : 0,
          y: hasSelection ? 0 : 20,
          pointerEvents: hasSelection ? 'auto' : 'none',
        }}
        transition={{ duration: reducedMotion ? 0.15 : 0.4, delay: hasSelection ? 0.08 : 0, ease }}
        data-testid="detail-gallery"
      >
        {activeClient && activeMedia.length > 0 && (
          <GalleryCarousel
            key={activeClient.id}
            items={activeMedia}
            reducedMotion={reducedMotion}
            variant={variant}
            clientId={activeClient.id}
          />
        )}
        <div className="quote-cta-wrap" data-testid="quote-cta-state3">
          <button
            className="quote-cta-detail"
            onClick={() => onOpenQuote?.(variant)}
          >
            <span>Obtenir un devis</span>
            <ArrowRight size={14} strokeWidth={1.5} />
          </button>
        </div>
      </motion.div>
    </div>
  );
};
